import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import './Study.css';
import '../styles/PageLayout.css';
import { studyAPI } from '../api/study';
import { resolveStaticMediaUrl } from '../utils/staticMedia';
import { put as apiPut } from '../api';
import { computePoemMatchScore, getReadEncouragement, getReciteEncouragement } from '../utils/poemScore';
import {
  clampSidebarWidth,
  getDeviceType,
  getResponsiveSidebarWidth,
  getStoredSidebarWidth,
  setStoredSidebarWidth
} from '../utils/pageLayout';
import { speakPoemText } from '../utils/speechVoice';
import UserInfo from '../components/UserInfo';
import '../components/UserInfo.css';
import PaymentModal from '../components/PaymentModal';
import { useShuTongContext } from '../context/ShuTongContext';

const STAGES = [
  { key: 'listen', label: '🎧 听古诗' },
  { key: 'read', label: '📖 读古诗' },
  { key: 'understand', label: '💡 了解' },
  { key: 'analyze', label: '🔍 分析' },
  { key: 'recite', label: '✍️ 背诵' }
];

const levelFromCount = (count) => {
  if (count < 10) return '童生';
  if (count < 30) return '秀才';
  if (count < 50) return '举人';
  if (count < 70) return '贡士';
  return '进士';
};

function Study() {
  const location = useLocation();
  const { setCurrentPoem } = useShuTongContext();
  const [progress, setProgress] = useState(0);
  const [level, setLevel] = useState('童生');
  const [selectedCategory, setSelectedCategory] = useState('textbook');
  const [textbooks, setTextbooks] = useState([]);
  const [otherCategories, setOtherCategories] = useState([]);
  const [textbookMembership, setTextbookMembership] = useState({});
  const [paymentModal, setPaymentModal] = useState({ open: false, poemId: null, poemTitle: '' });
  const [treeData, setTreeData] = useState({});
  const [expandedNodes, setExpandedNodes] = useState(new Set());
  const [selectedPoem, setSelectedPoem] = useState(null);
  const [currentStage, setCurrentStage] = useState(null);
  const [libraryPoems, setLibraryPoems] = useState([]);
  const [studyRecords, setStudyRecords] = useState([]);
  const [sidebarWidth, setSidebarWidth] = useState(getStoredSidebarWidth);
  const [isResizing, setIsResizing] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [listenCharIndex, setListenCharIndex] = useState(0);
  const utteranceRef = useRef(null);
  const listenProgressTimerRef = useRef(null);
  const listenStartTimeRef = useRef(0);

  const [isRecording, setIsRecording] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [recordingTime, setRecordingTime] = useState(0);
  const [recognizedText, setRecognizedText] = useState('');
  const [scoreResult, setScoreResult] = useState(null);
  const [encouragement, setEncouragement] = useState('');
  const [readAnalyzing, setReadAnalyzing] = useState(false);
  const [analysisDots, setAnalysisDots] = useState('');
  const recordingTimerRef = useRef(null);
  const countdownTimerRef = useRef(null);
  const readRecognitionRef = useRef(null);
  const readTranscriptRef = useRef('');
  const readInterimTranscriptRef = useRef('');

  const [isReciting, setIsReciting] = useState(false);
  const [reciteCountdown, setReciteCountdown] = useState(0);
  const [reciteTime, setReciteTime] = useState(0);
  const [reciteScoreResult, setReciteScoreResult] = useState(null);
  const [reciteEncouragement, setReciteEncouragement] = useState('');
  const [reciteUploading, setReciteUploading] = useState(false);
  const [reciteAnalyzing, setReciteAnalyzing] = useState(false);
  const [showHintModal, setShowHintModal] = useState(false);
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [completeSummary, setCompleteSummary] = useState({ count: 0, level: '童生' });
  const [showMindMapZoom, setShowMindMapZoom] = useState(false);
  const reciteMediaRecorderRef = useRef(null);
  const reciteAudioChunksRef = useRef([]);
  const reciteTimerRef = useRef(null);
  const reciteCountdownTimerRef = useRef(null);
  const reciteRecognitionRef = useRef(null);
  const reciteTranscriptRef = useRef('');
  const reciteInterimTranscriptRef = useRef('');

  const completedPoemIds = useRef(new Set());
  /** 已计入「已学习」数量的古诗 ID（有学习记录即计入，与刷新后 records.length 一致） */
  const countedPoemIds = useRef(new Set());
  const libraryPoemIds = useRef(new Set());
  const [poemTreeMap, setPoemTreeMap] = useState({});
  const [hasReadRecording, setHasReadRecording] = useState(false);

  const applyStudyRecords = useCallback((recordsData) => {
    const records = Array.isArray(recordsData) ? recordsData : [];
    const counted = new Set(
      records
        .map(r => Number(r.poemId ?? r.poem?.id))
        .filter(id => !Number.isNaN(id) && id > 0)
    );
    countedPoemIds.current = counted;
    completedPoemIds.current = new Set(
      records
        .filter(r => r.isCompleted === true || r.isCompleted === 1 || r.completed === true || r.completed === 1)
        .map(r => Number(r.poemId ?? r.poem?.id))
        .filter(id => !Number.isNaN(id) && id > 0)
    );
    // 完成记录一定算已背过
    completedPoemIds.current.forEach(id => counted.add(id));
    countedPoemIds.current = counted;

    const count = counted.size;
    setStudyRecords(records);
    setProgress(count);
    setLevel(levelFromCount(count));
    return { count, level: levelFromCount(count) };
  }, []);

  const refreshTreeData = useCallback(async () => {
    const treeDataResponse = await studyAPI.getTreeData().catch(() => null);
    if (!treeDataResponse) return;
    const { textbooks, categories, treeData: tree, poemTreeMap: map, textbookMembership: membership } = treeDataResponse;
    setTextbooks(Array.isArray(textbooks) ? textbooks : []);
    setOtherCategories(Array.isArray(categories) ? categories : []);
    setTreeData(tree || {});
    setPoemTreeMap(map || {});
    setTextbookMembership(membership || {});
  }, []);

  const openPaymentModal = (poem) => {
    if (textbookMembership?.payEnabled === false) {
      return;
    }
    setPaymentModal({
      open: true,
      poemId: poem.id,
      poemTitle: poem.title
    });
  };

  const handlePoemSelect = async (node) => {
    if (node.canStudy === false && node.membershipPayEnabled !== false) {
      if (node.lockReason === 'NEED_TEXTBOOK_PLAN') {
        openPaymentModal(node);
        return;
      }
      alert('该古诗需通过小书童添加并支付解锁后才能学习。');
      return;
    }
    setSelectedPoem({ id: node.id, title: node.title });
    setCurrentStage('listen');
    setHasReadRecording(false);
    setScoreResult(null);
    setReciteScoreResult(null);
    setReciteAnalyzing(false);
    try {
      const fullPoem = await studyAPI.getPoemDetail(node.id);
      if (fullPoem) setSelectedPoem(fullPoem);
    } catch (e) {
      const err = e?.response?.data;
      if (err?.errorCode === 'NEED_TEXTBOOK_PLAN') {
        openPaymentModal(node);
      } else if (err?.errorCode === 'NEED_POEM_PAY') {
        alert('该古诗需通过小书童添加并支付解锁后才能学习。');
      }
    }
  };

  const handlePaymentSuccess = async () => {
    const targetPoemId = paymentModal.poemId;
    setPaymentModal({ open: false, poemId: null, poemTitle: '' });
    await refreshTreeData();
    if (targetPoemId) {
      try {
        const fullPoem = await studyAPI.getPoemDetail(targetPoemId);
        if (fullPoem) {
          setSelectedPoem(fullPoem);
          setCurrentStage('listen');
        }
      } catch (e) {
        console.error('支付后加载古诗失败', e);
      }
    }
  };

  useEffect(() => {
    setShowMindMapZoom(false);
  }, [selectedPoem?.id, currentStage]);

  useEffect(() => {
    if (!showMindMapZoom) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setShowMindMapZoom(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showMindMapZoom]);

  useEffect(() => {
    if (!readAnalyzing && !reciteAnalyzing) {
      setAnalysisDots('');
      return undefined;
    }

    const timer = setInterval(() => {
      setAnalysisDots(prev => (prev.length >= 3 ? '' : `${prev}.`));
    }, 400);

    return () => clearInterval(timer);
  }, [readAnalyzing, reciteAnalyzing]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const poemsParam = urlParams.get('poems');
    if (poemsParam) {
      try {
        const poemIds = poemsParam.split(',').map(id => parseInt(id.trim())).filter(id => !isNaN(id));
        if (poemIds.length > 0) {
          const loadInitialPoem = async () => {
            try {
              const poemData = await studyAPI.getPoemDetail(poemIds[0]);
              if (poemData) {
                setSelectedPoem(poemData);
                setCurrentStage('listen');
              }
            } catch (e) {
              console.error('加载指定古诗失败', e);
            }
          };
          loadInitialPoem();
        }
      } catch (e) {
        console.error('解析URL参数失败', e);
      }
    }
  }, []);

  useEffect(() => {
    setCurrentPoem(selectedPoem);
    return () => setCurrentPoem(null);
  }, [selectedPoem, setCurrentPoem]);

  useEffect(() => {
    const addedPoemId = location.state?.addedPoemId;
    if (!addedPoemId) return;

    const refreshAfterAgent = async () => {
      try {
        const poemData = await studyAPI.getPoemDetail(addedPoemId);
        if (poemData) {
          setSelectedPoem(poemData);
          setCurrentStage('listen');
        }
        const treeDataResponse = await studyAPI.getTreeData().catch(() => null);
        const libData = await studyAPI.getUserLibrary().catch(() => []);
        if (treeDataResponse) {
          const { textbooks, categories, treeData: tree, poemTreeMap, textbookMembership: membership } = treeDataResponse;
          setTextbooks(Array.isArray(textbooks) ? textbooks : []);
          setOtherCategories(Array.isArray(categories) ? categories : []);
          setTreeData(tree || {});
          setPoemTreeMap(poemTreeMap || {});
          setTextbookMembership(membership || {});
        }
        const lib = Array.isArray(libData) ? libData : [];
        setLibraryPoems(lib);
        libraryPoemIds.current = new Set(lib.map(item => Number(item.poemId || item.id)));
      } catch (error) {
        console.error('刷新AI添加古诗失败', error);
      }
    };

    refreshAfterAgent();
    window.history.replaceState({}, document.title);
  }, [location.state?.addedPoemId]);

  useEffect(() => {
    const initData = async () => {
      try {
        const treeDataResponse = await studyAPI.getTreeData().catch(() => null);
        const recordsData = await studyAPI.getStudyRecords().catch(() => []);
        const libData = await studyAPI.getUserLibrary().catch(() => []);

        if (treeDataResponse) {
          const { textbooks, categories, treeData: tree, poemTreeMap, textbookMembership: membership } = treeDataResponse;

          setTextbooks(Array.isArray(textbooks) ? textbooks : []);
          setOtherCategories(Array.isArray(categories) ? categories : []);
          setTreeData(tree || {});
          setPoemTreeMap(poemTreeMap || {});
          setTextbookMembership(membership || {});
        }

        const records = Array.isArray(recordsData) ? recordsData : [];
        const lib = Array.isArray(libData) ? libData : [];
        setLibraryPoems(lib);
        applyStudyRecords(records);
        libraryPoemIds.current = new Set(lib.map(item => Number(item.poemId || item.id)));
      } catch (error) {
        console.error('初始化数据失败', error);
      }
    };
    initData();

    const handleWindowResize = () => {
      setSidebarWidth(getResponsiveSidebarWidth());
    };
    window.addEventListener('resize', handleWindowResize);
    return () => window.removeEventListener('resize', handleWindowResize);
  }, [applyStudyRecords]);

  const getPoemStatus = useCallback((poemId) => {
    const id = Number(poemId);
    if (completedPoemIds.current.has(id)) return 'completed';
    if (libraryPoemIds.current.has(id)) return 'loaded';
    return 'unloaded';
  }, []);

  const calcNodeProgress = useCallback((poemIds) => {
    if (!poemIds || poemIds.length === 0) return { done: 0, total: 0 };
    let done = 0;
    poemIds.forEach(id => {
      if (completedPoemIds.current.has(Number(id))) done++;
    });
    return { done, total: poemIds.length };
  }, []);

  const toggleNode = async (nodeKey, nodeType, nodeId) => {
    const newExpanded = new Set(expandedNodes);
    if (newExpanded.has(nodeKey)) {
      newExpanded.delete(nodeKey);
      setExpandedNodes(newExpanded);
      return;
    }
    newExpanded.add(nodeKey);
    setExpandedNodes(newExpanded);

    const cacheKey = `${nodeType}-${nodeId}`;
    if (treeData[cacheKey]) return;

    try {
      let data;
      switch (nodeType) {
        case 'textbook':
          data = await studyAPI.getGradesByTextbook(nodeId); break;
        case 'grade':
          data = await studyAPI.getUnitsByGrade(nodeId); break;
        case 'unit':
          data = await studyAPI.getPoemsByUnit(nodeId); break;
        case 'category':
          data = await studyAPI.getSubItemsByCategory(nodeId); break;
        case 'subitem':
          data = await studyAPI.getPoemsBySubItem(nodeId); break;
        default:
          return;
      }
      data = Array.isArray(data) ? data : [];
      setTreeData(prev => ({ ...prev, [cacheKey]: data }));
    } catch (error) {
      console.error(`加载 ${nodeType} ${nodeId} 失败`, error);
      newExpanded.delete(nodeKey);
      setExpandedNodes(newExpanded);
    }
  };

  const renderTreeNode = (node, nodeType, depth = 0) => {
    const nodeKey = `${nodeType}-${node.id}`;
    const isExpanded = expandedNodes.has(nodeKey);
    const isLeaf = nodeType === 'poem';

    let status = 'unloaded';
    let progressText = '';

    if (!isLeaf) {
      let done = node.progressDone || 0;
      let total = node.progressTotal || 0;

      if (total === 0) {
        const children = treeData[`${nodeType}-${node.id}`] || [];
        let allPoems = [];
        
        const collectPoems = (nodes, type) => {
          nodes.forEach(n => {
            if (type === 'poem' || (nodeType === 'textbook' && type === 'unit' && n.poems) ||
                (nodeType === 'grade' && type === 'unit' && n.poems) ||
                (nodeType === 'category' && type === 'subitem' && n.poems) ||
                (nodeType === 'category' && type === 'poem') ||
                (nodeType === 'subitem' && type === 'poem')) {
              if (n.poems) {
                n.poems.forEach(p => allPoems.push(p));
              } else if (type === 'poem') {
                allPoems.push(n);
              }
            }
            if (n.grades) collectPoems(n.grades, 'grade');
            if (n.units) collectPoems(n.units, 'unit');
            if (n.subItems) collectPoems(n.subItems, 'subitem');
            if (n.poems && nodeType !== 'unit' && nodeType !== 'subitem') collectPoems(n.poems, 'poem');
          });
        };

        if (nodeType === 'textbook' && node.grades) collectPoems(node.grades, 'grade');
        else if (nodeType === 'grade' && node.units) collectPoems(node.units, 'unit');
        else if (nodeType === 'unit' && node.poems) allPoems = node.poems;
        else if (nodeType === 'category' && node.subItems) collectPoems(node.subItems, 'subitem');
        else if (nodeType === 'subitem' && node.poems) allPoems = node.poems;
        else collectPoems(children, nodeType === 'textbook' ? 'grade' : nodeType === 'grade' ? 'unit' : nodeType === 'category' ? 'subitem' : 'poem');

        total = allPoems.length;
        done = allPoems.filter(p => p.completed).length;
      }

      if (total > 0) {
        progressText = ` (${done}/${total})`;
        if (done === total) status = 'completed';
        else if (done > 0) status = 'loaded';
      }
    } else {
      status = getPoemStatus(node.id);
    }

    const statusColor = status === 'completed' ? '#52c41a' : status === 'loaded' ? '#1890ff' : '#999';
    const isSelected = selectedPoem && selectedPoem.id === node.id;

    return (
      <div key={nodeKey} className={`tree-node tree-node-${nodeType}`} style={{ paddingLeft: depth * 16 + 8 }}>
        <div
          className={`tree-node-label ${isSelected ? 'selected' : ''}`}
          style={{ color: statusColor }}
          onClick={() => {
            if (isLeaf) {
              handlePoemSelect(node);
            } else {
              toggleNode(nodeKey, nodeType, node.id);
            }
          }}
        >
          {!isLeaf && (
            <span className="tree-node-arrow">{isExpanded ? '▼' : '▶'}</span>
          )}
          <span className={`tree-node-text status-${status}`}>
            {status === 'completed' && <><span className="tree-check">✅</span><span className="tree-read-tag">已读</span></>}
            {isLeaf && node.canStudy === false && <span className="tree-lock">🔒</span>}
            {node.name || node.title}
            {progressText}
          </span>
        </div>
        {!isLeaf && isExpanded && treeData[`${nodeType}-${node.id}`] && (
          <div className="tree-node-children">
            {treeData[`${nodeType}-${node.id}`].map(child => {
              const childType = nodeType === 'textbook' ? 'grade' : nodeType === 'grade' ? 'unit' :
                nodeType === 'unit' ? 'poem' : nodeType === 'category' ? 'subitem' : 'poem';
              return renderTreeNode(child, childType, depth + 1);
            })}
          </div>
        )}
      </div>
    );
  };

  const handleResizeStart = (e) => {
    if (getDeviceType() === 'mobile') return;
    e.preventDefault();
    setIsResizing(true);
    const startX = e.type.startsWith('touch') ? e.touches[0].clientX : e.clientX;
    const startWidth = typeof sidebarWidth === 'number' ? sidebarWidth : parseInt(sidebarWidth, 10) || 280;

    const handleMove = (moveEvent) => {
      const clientX = moveEvent.type.startsWith('touch') ? moveEvent.touches[0].clientX : moveEvent.clientX;
      const diff = clientX - startX;
      const device = getDeviceType();
      const newWidth = clampSidebarWidth(startWidth + diff, device);
      setSidebarWidth(newWidth);
      if (device !== 'mobile') {
        setStoredSidebarWidth(newWidth);
      }
    };

    const handleUp = () => {
      setIsResizing(false);
      document.removeEventListener('mousemove', handleMove);
      document.removeEventListener('mouseup', handleUp);
      document.removeEventListener('touchmove', handleMove);
      document.removeEventListener('touchend', handleUp);
    };

    document.addEventListener('mousemove', handleMove);
    document.addEventListener('mouseup', handleUp);
    document.addEventListener('touchmove', handleMove);
    document.addEventListener('touchend', handleUp);
  };

  const getCurrentStageIndex = () => {
    return STAGES.findIndex(s => s.key === currentStage);
  };

  const handlePrevStage = () => {
    const idx = getCurrentStageIndex();
    if (idx > 0) setCurrentStage(STAGES[idx - 1].key);
  };

  const handleNextStage = async () => {
    if (currentStage === 'recite') {
      try {
        const poemId = Number(selectedPoem.id);

        await studyAPI.createStudyRecord({ poemId: selectedPoem.id });
        // 每次背诵都加 1 积分
        try { await apiPut('/api/user/add-score?points=1'); window.dispatchEvent(new Event('score-updated')); } catch (e) { console.error('加分失败', e); }

        // 以服务端学习记录为准同步数量（重复背诵不会新增记录，数量不变）
        let summary = { count: progress, level };
        try {
          const recordsData = await studyAPI.getStudyRecords();
          summary = applyStudyRecords(recordsData);
        } catch (e) {
          console.error('刷新学习记录失败', e);
          // 兜底：仅本地判断，避免 UI 误加
          if (!countedPoemIds.current.has(poemId)) {
            countedPoemIds.current.add(poemId);
            const newCount = progress + 1;
            summary = { count: newCount, level: levelFromCount(newCount) };
            setProgress(newCount);
            setLevel(summary.level);
          }
        }
        completedPoemIds.current.add(poemId);
        setCompleteSummary(summary);
        setShowCompleteModal(true);
      } catch (error) {
        console.error('完成学习失败', error);
        setCompleteSummary({ count: progress, level });
        setShowCompleteModal(true);
      }
    } else {
      const idx = getCurrentStageIndex();
      if (idx >= 0 && idx < STAGES.length - 1) setCurrentStage(STAGES[idx + 1].key);
    }
  };

  const toggleTTS = async () => {
    if (!selectedPoem) return;
    if (isPlaying) {
      window.speechSynthesis.cancel();
      clearListenProgressTimer();
      setIsPlaying(false);
      setListenCharIndex(0);
      return;
    }

    const speakText = (selectedPoem.content || selectedPoem.title || '').trim();
    if (!speakText) return;

    clearListenProgressTimer();
    setListenCharIndex(0);
    setIsPlaying(true);
    try {
      utteranceRef.current = await speakPoemText(speakText, {
        onStart: () => startListenProgressTimer(speakText),
        onBoundary: (event) => {
          if (typeof event.charIndex === 'number') {
            setListenCharIndex(prev => Math.max(prev, event.charIndex));
          }
        },
        onEnd: () => {
          clearListenProgressTimer();
          setListenCharIndex(speakText.length);
          setIsPlaying(false);
        },
        onError: () => {
          clearListenProgressTimer();
          setIsPlaying(false);
        }
      });
    } catch (error) {
      console.error('播放朗读失败', error);
      clearListenProgressTimer();
      setIsPlaying(false);
    }
  };

  useEffect(() => {
    return () => {
      window.speechSynthesis.cancel();
      clearListenProgressTimer();
    };
  }, []);

  useEffect(() => {
    window.speechSynthesis.cancel();
    clearListenProgressTimer();
    setIsPlaying(false);
    setListenCharIndex(0);
  }, [selectedPoem?.id, currentStage]);

  const formatPoemContent = (content) => {
    if (!content) return [];
    return content.split('\n').filter(line => line.trim());
  };

  function clearListenProgressTimer() {
    if (listenProgressTimerRef.current) {
      clearInterval(listenProgressTimerRef.current);
      listenProgressTimerRef.current = null;
    }
  }

  function startListenProgressTimer(text) {
    clearListenProgressTimer();
    listenStartTimeRef.current = Date.now();
    const readableCount = (text.match(/[\u4e00-\u9fa5a-zA-Z0-9]/g) || []).length;
    const estimatedDuration = Math.max(5000, readableCount * 430);

    listenProgressTimerRef.current = setInterval(() => {
      const elapsed = Date.now() - listenStartTimeRef.current;
      const estimatedIndex = Math.min(text.length, Math.floor((elapsed / estimatedDuration) * text.length));
      setListenCharIndex(prev => Math.max(prev, estimatedIndex));
      if (estimatedIndex >= text.length) clearListenProgressTimer();
    }, 120);
  }

  const getPoemLinesWithSpeechIndexes = (content) => {
    if (!content) return [];
    const trimmedContent = content.trim();
    let searchStart = 0;

    return trimmedContent
      .split('\n')
      .map(rawLine => {
        const text = rawLine.trim();
        if (!text) {
          searchStart += rawLine.length + 1;
          return null;
        }

        const startIndex = trimmedContent.indexOf(text, searchStart);
        searchStart = (startIndex >= 0 ? startIndex : searchStart) + rawLine.length + 1;
        return {
          text,
          startIndex: startIndex >= 0 ? startIndex : 0
        };
      })
      .filter(Boolean);
  };

  const renderListenPoemContent = (content) => (
    getPoemLinesWithSpeechIndexes(content).map((line, lineIndex) => {
      let charOffset = 0;

      return (
        <div key={lineIndex} className="kaiti-line listen-kaiti-line">
          {Array.from(line.text).map((char, charIndex) => {
            const charStart = line.startIndex + charOffset;
            const charEnd = charStart + char.length;
            charOffset += char.length;

            const isActive = isPlaying && listenCharIndex >= charStart && listenCharIndex < charEnd;
            const isRead = listenCharIndex >= charEnd;

            return (
              <span
                key={`${char}-${charIndex}`}
                className={`listen-char ${isRead ? 'read' : ''} ${isActive ? 'active' : ''}`}
              >
                {char}
              </span>
            );
          })}
        </div>
      );
    })
  );

  const startReading = () => {
    setScoreResult(null);
    setEncouragement('');
    setRecognizedText('');
    setReadAnalyzing(false);
    readTranscriptRef.current = '';
    readInterimTranscriptRef.current = '';
    setCountdown(3);
    countdownTimerRef.current = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(countdownTimerRef.current);
          setTimeout(() => startRecording(), 100);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const startRecording = async () => {
    try {
      await navigator.mediaDevices.getUserMedia({ audio: true });
      startReadSpeechRecognition();
      setIsRecording(true);
      setRecordingTime(0);

      recordingTimerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);

      setTimeout(() => {
        if (readRecognitionRef.current) stopRecording();
      }, 60000);
    } catch (err) {
      console.error('获取麦克风权限失败', err);
      alert('无法访问麦克风，请检查浏览器权限设置');
    }
  };

  const stopRecording = () => {
    stopReadSpeechRecognition();
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
    }
    setIsRecording(false);
    setReadAnalyzing(true);
    setRecognizedText('正在分析您的朗读');
    setTimeout(() => {
      applyReadScore(getReadTranscript());
    }, 300);
  };

  const startReadSpeechRecognition = () => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    readTranscriptRef.current = '';
    readInterimTranscriptRef.current = '';

    if (!SpeechRecognition) {
      console.warn('当前浏览器不支持语音识别，朗读分数将无法根据语音内容计算');
      return;
    }

    try {
      const recognition = new SpeechRecognition();
      recognition.lang = 'zh-CN';
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.maxAlternatives = 1;

      recognition.onresult = (event) => {
        let interim = '';
        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript;
          if (event.results[i].isFinal) {
            readTranscriptRef.current += transcript;
          } else {
            interim += transcript;
          }
        }
        readInterimTranscriptRef.current = interim;
      };

      recognition.onerror = (event) => {
        if (event.error !== 'aborted') {
          console.error('朗读语音识别失败', event.error);
        }
      };

      readRecognitionRef.current = recognition;
      recognition.start();
    } catch (error) {
      console.error('启动朗读语音识别失败', error);
    }
  };

  const stopReadSpeechRecognition = () => {
    if (!readRecognitionRef.current) return;

    try {
      readRecognitionRef.current.stop();
    } catch (error) {
      console.error('停止朗读语音识别失败', error);
    } finally {
      readRecognitionRef.current = null;
    }
  };

  const getReadTranscript = () => (
    readTranscriptRef.current + readInterimTranscriptRef.current
  ).trim();

  const applyReadScore = (recognizedText) => {
    const standardText = selectedPoem.content || '';
    const result = computePoemMatchScore(recognizedText, standardText);
    setReadAnalyzing(false);
    setRecognizedText(recognizedText || '（未识别到有效内容）');
    setScoreResult(result);
    setEncouragement(getReadEncouragement(result.score));
    setHasReadRecording(true);
  };

  const applyReciteScore = (recognizedText, audioBlob) => {
    const standardText = selectedPoem.content || '';
    const result = computePoemMatchScore(recognizedText, standardText);
    setReciteAnalyzing(false);
    setReciteScoreResult(result);
    setReciteEncouragement(getReciteEncouragement(result.score));
    uploadReciteRecording(audioBlob, result.score);
  };

  const startReciteSpeechRecognition = () => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    reciteTranscriptRef.current = '';
    reciteInterimTranscriptRef.current = '';

    if (!SpeechRecognition) {
      console.warn('当前浏览器不支持语音识别，背诵分数将无法根据语音内容计算');
      return;
    }

    try {
      const recognition = new SpeechRecognition();
      recognition.lang = 'zh-CN';
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.maxAlternatives = 1;

      recognition.onresult = (event) => {
        let interim = '';
        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript;
          if (event.results[i].isFinal) {
            reciteTranscriptRef.current += transcript;
          } else {
            interim += transcript;
          }
        }
        reciteInterimTranscriptRef.current = interim;
      };

      recognition.onerror = (event) => {
        if (event.error !== 'aborted') {
          console.error('背诵语音识别失败', event.error);
        }
      };

      reciteRecognitionRef.current = recognition;
      recognition.start();
    } catch (error) {
      console.error('启动背诵语音识别失败', error);
    }
  };

  const stopReciteSpeechRecognition = () => {
    if (!reciteRecognitionRef.current) return;

    try {
      reciteRecognitionRef.current.stop();
    } catch (error) {
      console.error('停止背诵语音识别失败', error);
    } finally {
      reciteRecognitionRef.current = null;
    }
  };

  const getReciteTranscript = () => (
    reciteTranscriptRef.current + reciteInterimTranscriptRef.current
  ).trim();

  const startReciting = () => {
    setReciteScoreResult(null);
    setReciteEncouragement('');
    setReciteAnalyzing(false);
    setReciteCountdown(3);
    reciteCountdownTimerRef.current = setInterval(() => {
      setReciteCountdown(prev => {
        if (prev <= 1) {
          clearInterval(reciteCountdownTimerRef.current);
          setTimeout(() => startReciteRecording(), 100);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const startReciteRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      reciteAudioChunksRef.current = [];
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : 'audio/webm';
      const mediaRecorder = new MediaRecorder(stream, { mimeType });
      reciteMediaRecorderRef.current = mediaRecorder;

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) reciteAudioChunksRef.current.push(e.data);
      };

      mediaRecorder.onstop = () => {
        stream.getTracks().forEach(track => track.stop());
        stopReciteSpeechRecognition();
        const blob = new Blob(reciteAudioChunksRef.current, { type: mimeType });
        setReciteAnalyzing(true);
        setTimeout(() => {
          applyReciteScore(getReciteTranscript(), blob);
        }, 300);
      };

      startReciteSpeechRecognition();
      mediaRecorder.start();
      setIsReciting(true);
      setReciteTime(0);

      reciteTimerRef.current = setInterval(() => {
        setReciteTime(prev => prev + 1);
      }, 1000);

      setTimeout(() => {
        if (mediaRecorder.state === 'recording') stopReciteRecording();
      }, 60000);
    } catch (err) {
      console.error('获取麦克风权限失败', err);
      alert('无法访问麦克风，请检查浏览器权限设置');
    }
  };

  const stopReciteRecording = () => {
    if (reciteMediaRecorderRef.current && reciteMediaRecorderRef.current.state === 'recording') {
      reciteMediaRecorderRef.current.stop();
    }
    stopReciteSpeechRecognition();
    if (reciteTimerRef.current) {
      clearInterval(reciteTimerRef.current);
    }
    setIsReciting(false);
  };

  const uploadReciteRecording = async (audioBlob, score) => {
    if (!selectedPoem || !selectedPoem.id) return;
    setReciteUploading(true);
    try {
      await studyAPI.saveRecording(audioBlob, selectedPoem.id, score);
    } catch (error) {
      console.error('上传背诵录音失败', error);
    } finally {
      setReciteUploading(false);
    }
  };

  const getMindMapImageSrc = (mindMapUrl) => resolveStaticMediaUrl(mindMapUrl, 'mindMap');

  const getBackgroundVideoSrc = (backgroundVideo) => resolveStaticMediaUrl(backgroundVideo, 'video');

  const preventMediaContextMenu = (event) => {
    event.preventDefault();
  };

  const handleMindMapImageError = (e) => {
    const image = e.currentTarget;
    const currentSrc = image.getAttribute('src') || '';

    if (!image.dataset.triedJpg && /\.jpeg($|\?)/i.test(currentSrc)) {
      image.dataset.triedJpg = 'true';
      image.src = currentSrc.replace(/\.jpeg($|\?)/i, '.jpg$1');
      return;
    }

    image.style.display = 'none';
  };

  const renderStepProgress = () => {
    const currentIdx = getCurrentStageIndex();
    return (
      <div className="step-progress">
        {STAGES.map((stage, idx) => (
          <React.Fragment key={stage.key}>
            <div className={`step-item ${idx === currentIdx ? 'current' : ''} ${idx < currentIdx ? 'done' : ''} ${idx > currentIdx ? 'pending' : ''}`}>
              <div className="step-circle">
                {idx < currentIdx ? '✅' : idx + 1}
              </div>
              <div className="step-label">{stage.label}</div>
            </div>
            {idx < STAGES.length - 1 && (
              <div className={`step-line ${idx < currentIdx ? 'done' : idx === currentIdx ? 'current' : 'pending'}`} />
            )}
          </React.Fragment>
        ))}
      </div>
    );
  };

  return (
    <div className="study-container page-container">
      <div className="study-header page-header">
        <div className="progress-section">
          <h2>学习进度</h2>
          <div className="progress-info">
            <span>已学习：{progress} 首</span>
            <span>等级：{level}</span>
          </div>
          <div className="progress-bar">
            <div
              className="progress-fill"
              style={{ width: `${Math.min((progress / 70) * 100, 100)}%` }}
            />
          </div>
        </div>
        <UserInfo studyCount={progress} currentPage="study" />
      </div>

      <div className="study-content page-content">
        <div
          className="category-sidebar page-sidebar"
          style={{ width: getDeviceType() === 'mobile' ? '100%' : `${sidebarWidth}px`, position: 'relative' }}
        >
          <div className="category-tabs">
            <button
              className={`category-tab ${selectedCategory === 'textbook' ? 'active' : ''}`}
              onClick={() => setSelectedCategory('textbook')}
            >
              📚 教材体系
            </button>
            <button
              className={`category-tab ${selectedCategory === 'other' ? 'active' : ''}`}
              onClick={() => setSelectedCategory('other')}
            >
              🏷️ 其他分类
            </button>
          </div>

          {textbookMembership?.payEnabled && textbookMembership?.active && selectedCategory === 'textbook' && (
            <p className="membership-hint">教材套餐有效至 {textbookMembership.expireAt ? new Date(textbookMembership.expireAt).toLocaleDateString() : ''}</p>
          )}

          <div className="tree-container">
            {selectedCategory === 'textbook' ? (
              textbooks.length > 0
                ? textbooks.map(tb => renderTreeNode(tb, 'textbook', 0))
                : <p className="tree-empty">暂无已加载的教材</p>
            ) : (
              otherCategories.length > 0
                ? otherCategories.map(cat => renderTreeNode(cat, 'category', 0))
                : <p className="tree-empty">暂无已加载的分类</p>
            )}
          </div>

          {getDeviceType() !== 'mobile' && (
            <div className="sidebar-resize-handle" onMouseDown={handleResizeStart} onTouchStart={handleResizeStart} />
          )}
        </div>

        <div className="study-main page-main">
          {selectedPoem && currentStage ? (
            <div className="poem-study study-learning-panel">
              <div className="poem-study-header">
                <h3>{selectedPoem.title}</h3>
                <h4>{selectedPoem.author}{selectedPoem.dynasty ? ` · ${selectedPoem.dynasty}` : ''}</h4>
              </div>

              {renderStepProgress()}

              <div className="stage-content-area">
                {currentStage === 'listen' && (
                  <div className="study-stage">
                    <h4>听古诗</h4>
                    <div className="poem-display-kaiti">
                      {renderListenPoemContent(selectedPoem.content || selectedPoem.title || '')}
                    </div>
                    <button className={`btn tts-btn ${isPlaying ? 'playing' : ''}`} onClick={toggleTTS}>
                      {isPlaying ? '⏸ 暂停' : '▶ 播放朗读'}
                    </button>
                  </div>
                )}

                {currentStage === 'read' && (
                  <div className="study-stage">
                    <h4>读古诗</h4>
                    <div className="poem-display-kaiti poem-display-read">
                      {formatPoemContent(selectedPoem.content).map((line, i) => (
                        <div key={i} className="kaiti-line">{line}</div>
                      ))}
                    </div>

                    <div className="recording-area">
                      {hasReadRecording ? (
                        <div className="has-recording-prompt">
                          <p className="recording-exists-hint">✅ 您已完成《{selectedPoem.title}》的朗读练习</p>
                          <div className="recite-direct-actions">
                            <button className="btn btn-record-start" onClick={() => {
                              const nextStageIdx = STAGES.findIndex(s => s.key === 'recite');
                              if (nextStageIdx > -1) setCurrentStage('recite');
                            }}>
                              ✍️ 直接背诵古诗
                            </button>
                            <button className="btn hint-btn" onClick={() => setShowHintModal(true)}>
                              💡 查看提示
                            </button>
                          </div>
                        </div>
                      ) : (
                        <>
                          {!isRecording && !scoreResult && !readAnalyzing && countdown === 0 && (
                            <button className="btn btn-record-start" onClick={startReading}>
                              🎤 开始朗读
                            </button>
                          )}

                          {countdown > 0 && (
                            <div className="countdown-display">
                              <div className="countdown-number">{countdown}</div>
                              <p>准备开始...</p>
                            </div>
                          )}

                          {isRecording && (
                            <div className="recording-active">
                              <div className="recording-indicator">🔴 录音中... {recordingTime}秒</div>
                              <p>请大声朗读《{selectedPoem.title}》</p>
                              <button className="btn btn-stop" onClick={stopRecording}>
                                ⏹ 停止录音
                              </button>
                            </div>
                          )}

                          {readAnalyzing && (
                            <div className="recording-active">
                              <p>正在分析您的朗读{analysisDots}</p>
                            </div>
                          )}

                          {scoreResult && (
                            <div className="score-result">
                              <div className={`score-circle ${scoreResult.score >= 90 ? 'excellent' : scoreResult.score >= 70 ? 'good' : scoreResult.score >= 50 ? 'ok' : 'keep-going'}`}>
                                <span className="score-number">{scoreResult.score}</span>
                                <span className="score-unit">分</span>
                              </div>
                              <p className="encouragement-text">{encouragement}</p>
                              <div className="score-detail">
                                <span>准确率：{scoreResult.accuracy}</span>
                              </div>
                              <button className="btn btn-retry" onClick={() => {
                                setScoreResult(null);
                                setEncouragement('');
                                setReadAnalyzing(false);
                                setHasReadRecording(false);
                              }}>
                                🔄 再试一次
                              </button>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                )}

                {currentStage === 'understand' && (
                  <div className="study-stage">
                    <h4>了解古诗</h4>
                    <div className="stage-content">
                      {selectedPoem.backgroundVideo && (
                        <div
                          className="video-wrapper protected-video-wrapper"
                          onContextMenu={preventMediaContextMenu}
                        >
                          <video
                            id="understand-video"
                            className="understand-video"
                            src={getBackgroundVideoSrc(selectedPoem.backgroundVideo)}
                            controls
                            controlsList="nodownload noremoteplayback"
                            disablePictureInPicture
                            disableRemotePlayback
                            autoPlay
                            muted
                            playsInline
                            preload="metadata"
                            onContextMenu={preventMediaContextMenu}
                            onDragStart={preventMediaContextMenu}
                            onFullscreenChange={(e) => {
                              const btn = e.target.parentElement.querySelector('.video-fullscreen-btn');
                              if (btn) btn.textContent = document.fullscreenElement ? '⛶ 还原' : '⛶ 全屏';
                            }}
                          />
                          <button
                            className="video-fullscreen-btn"
                            onClick={() => {
                              const video = document.getElementById('understand-video');
                              if (!video) return;
                              if (document.fullscreenElement) {
                                document.exitFullscreen();
                              } else {
                                video.requestFullscreen().catch(() => {});
                              }
                            }}
                          >
                            ⛶ 全屏
                          </button>
                        </div>
                      )}
                      <p>{selectedPoem.translation || selectedPoem.explanation || '《' + selectedPoem.title + '》是一首优美的古诗词作品，请仔细品味其中的意境与情感。'}</p>
                      {selectedPoem.annotation && (
                        <div className="annotation-section">
                          <h5>注释</h5>
                          <p>{selectedPoem.annotation}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {currentStage === 'analyze' && (
                  <div className="study-stage">
                    <h4>分析古诗</h4>
                    <div className="stage-content">
                      <div className="mind-map">
                        <div className="mind-map-root">{selectedPoem.title}</div>
                        {selectedPoem.mindMapUrl && (
                          <div
                            className="mind-map-image-wrapper mind-map-image-clickable"
                            onClick={() => setShowMindMapZoom(true)}
                            onKeyDown={(event) => {
                              if (event.key === 'Enter' || event.key === ' ') {
                                event.preventDefault();
                                setShowMindMapZoom(true);
                              }
                            }}
                            role="button"
                            tabIndex={0}
                            aria-label={`放大查看《${selectedPoem.title}》思维导图`}
                          >
                            <img
                              className="mind-map-image"
                              src={getMindMapImageSrc(selectedPoem.mindMapUrl)}
                              alt={`${selectedPoem.title}思维导图`}
                              onError={handleMindMapImageError}
                              draggable={false}
                            />
                            <span className="mind-map-zoom-hint">🔍 点击放大查看</span>
                          </div>
                        )}
                        <div className="mind-map-branches">
                          <div className="mind-map-branch">
                            <div className="mind-map-node">意境</div>
                            <p className="mind-map-desc">{selectedPoem.imagery || '体会诗歌描绘的画面与氛围'}</p>
                          </div>
                          <div className="mind-map-branch">
                            <div className="mind-map-node">情感</div>
                            <p className="mind-map-desc">{selectedPoem.emotion || '感受诗人表达的思想感情'}</p>
                          </div>
                          <div className="mind-map-branch">
                            <div className="mind-map-node">手法</div>
                            <p className="mind-map-desc">{selectedPoem.technique || '分析诗歌运用的修辞与表现手法'}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {currentStage === 'recite' && (
                  <div className="study-stage">
                    <h4>✍️ 背诵古诗</h4>
                    <div className="stage-content">
                      <div className="recite-area">
                        <p className="recite-prompt">请尝试背诵《{selectedPoem.title}》</p>

                        {!isReciting && !reciteScoreResult && !reciteAnalyzing && reciteCountdown === 0 && (
                          <div className="recite-actions">
                            <button className="btn btn-record-start" onClick={startReciting}>
                              🎤 开始背诵录音
                            </button>
                            <button className="btn hint-btn" onClick={() => setShowHintModal(true)}>
                              💡 查看提示
                            </button>
                          </div>
                        )}

                        {reciteCountdown > 0 && (
                          <div className="countdown-display">
                            <div className="countdown-number">{reciteCountdown}</div>
                            <p>准备开始背诵...</p>
                          </div>
                        )}

                        {isReciting && (
                          <div className="recording-active">
                            <div className="recording-indicator">🔴 背诵录音中... {reciteTime}秒</div>
                            <p>请背诵《{selectedPoem.title}》</p>
                            <button className="btn btn-stop" onClick={stopReciteRecording}>
                              ⏹ 停止录音
                            </button>
                          </div>
                        )}

                        {reciteAnalyzing && (
                          <div className="recording-active">
                            <p>正在分析您的背诵{analysisDots}</p>
                          </div>
                        )}

                        {reciteScoreResult && (
                          <div className="score-result">
                            <div className={`score-circle ${reciteScoreResult.score >= 90 ? 'excellent' : reciteScoreResult.score >= 70 ? 'good' : reciteScoreResult.score >= 50 ? 'ok' : 'keep-going'}`}>
                              <span className="score-number">{reciteScoreResult.score}</span>
                              <span className="score-unit">分</span>
                            </div>
                            <p className="encouragement-text">{reciteEncouragement}</p>
                            <div className="score-detail">
                              <span>准确率：{reciteScoreResult.accuracy}</span>
                              {!reciteUploading && <span className="upload-status">✅ 背诵录音已保存</span>}
                              {reciteUploading && <span className="upload-status">⏳ 正在保存背诵录音...</span>}
                            </div>
                            <div className="recite-actions">
                              <button className="btn hint-btn" onClick={() => setShowHintModal(true)}>
                                💡 查看提示
                              </button>
                              <button className="btn btn-retry" onClick={() => { setReciteScoreResult(null); setReciteEncouragement(''); setReciteAnalyzing(false); }}>
                                🔄 再背一次
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="stage-actions">
                <button className="btn btn-prev" onClick={handlePrevStage} disabled={currentStage === 'listen'}>
                  ← 上一步
                </button>
                <button className="btn btn-next" onClick={handleNextStage}>
                  {currentStage === 'recite' ? '完成学习' : '下一步 →'}
                </button>
              </div>
            </div>
          ) : (
            <div className="study-placeholder">
              <div className="placeholder-icon">📖</div>
              <p>请从左侧选择一首古诗开始学习</p>
            </div>
          )}
        </div>
      </div>

      {showMindMapZoom && selectedPoem?.mindMapUrl && (
        <div
          className="mind-map-zoom-overlay"
          onClick={() => setShowMindMapZoom(false)}
          role="presentation"
        >
          <div className="mind-map-zoom-panel" onClick={event => event.stopPropagation()}>
            <button
              type="button"
              className="mind-map-zoom-close"
              onClick={() => setShowMindMapZoom(false)}
            >
              ✕ 关闭
            </button>
            <img
              className="mind-map-zoom-image"
              src={getMindMapImageSrc(selectedPoem.mindMapUrl)}
              alt={`${selectedPoem.title}思维导图`}
              draggable={false}
            />
            <p className="mind-map-zoom-tip">点击空白处或关闭按钮缩小</p>
          </div>
        </div>
      )}

      {showHintModal && (
        <div className="modal-overlay" onClick={() => setShowHintModal(false)}>
          <div className="lively-modal hint-modal" onClick={e => e.stopPropagation()}>
            <h3 className="modal-title">💡 古诗提示</h3>
            <div className="modal-body">
              <div className="poem-display-kaiti" style={{ fontSize: '18px', lineHeight: '2', padding: '15px' }}>
                {formatPoemContent(selectedPoem.content).map((line, i) => (
                  <div key={i} className="kaiti-line">{line}</div>
                ))}
              </div>
            </div>
            <button className="confirm-btn" onClick={() => setShowHintModal(false)}>知道了</button>
          </div>
        </div>
      )}

      {showCompleteModal && (
        <div className="modal-overlay" onClick={() => { setShowCompleteModal(false); setSelectedPoem(null); setCurrentStage(null); }}>
          <div className="lively-modal complete-modal" onClick={e => e.stopPropagation()}>
            <div className="complete-icon">🎉</div>
            <h3 className="modal-title">恭喜完成学习！</h3>
            <div className="modal-body">
              <p className="complete-poem-name">《{selectedPoem?.title}》</p>
              <p className="complete-reward">🪙 获得 <strong>1</strong> 个元宝积分</p>
              <p className="complete-stats">已学习：<strong>{completeSummary.count}</strong> 首 | 等级：<strong>{completeSummary.level}</strong></p>
            </div>
            <button className="confirm-btn" onClick={() => { setShowCompleteModal(false); setSelectedPoem(null); setCurrentStage(null); }}>
              继续学习
            </button>
          </div>
        </div>
      )}

      <PaymentModal
        open={paymentModal.open}
        type="TEXTBOOK_PLAN"
        poemId={paymentModal.poemId}
        poemTitle={paymentModal.poemTitle}
        returnPath={`${location.pathname}${location.search}`}
        onClose={() => setPaymentModal({ open: false, poemId: null, poemTitle: '' })}
        onSuccess={handlePaymentSuccess}
      />
    </div>
  );
}

export default Study;
